import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { PencilIcon, PlusIcon } from "lucide-react";
import {
  ExternalTestPartnerStatusEnum,
  type ExternalTestPartnerId,
} from "@brewbase/db/types";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SimpleSelect } from "@/components/ui/simple-select";
import { RequireCapability } from "@/components/require-capability";
import { ConfirmActionDialog } from "@/components/confirm-action-dialog";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { formatDate } from "@/lib/date";
import { cn } from "@/lib/utils";

/**
 * The laboratories we send samples to.
 *
 * The `code` is not decoration: an external test instance has no template, so
 * the lab's code stands in as the test's code — in the instance name
 * ("P59H1-eurofins"), in the sample formula grammar's middle level, and as the
 * key the import adapters are registered under. Changing it renames a test,
 * which is why rows are read-only at rest and a code change is confirmed with
 * the number of orders it touches.
 */

export const Route = createFileRoute("/analytics/labs")({
  component: () => (
    <RequireCapability cap="test-template:edit">
      <ExternalLabsAdmin />
    </RequireCapability>
  ),
});

type Partner = RouterOutputs["tests"]["externalPartners"]["list"][number];

const STATUS_OPTIONS = [
  { value: ExternalTestPartnerStatusEnum.ACTIVE, label: "Active" },
  { value: ExternalTestPartnerStatusEnum.NEEDS_REVIEW, label: "Needs review" },
  { value: ExternalTestPartnerStatusEnum.INACTIVE, label: "Inactive" },
];
const statusLabel = (s: ExternalTestPartnerStatusEnum) =>
  STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;

function usageSummary(p: Partner): string {
  const parts: string[] = [];
  if (p.order_count) parts.push(`${p.order_count} order${p.order_count === 1 ? "" : "s"}`);
  if (p.alias_count) parts.push(`${p.alias_count} lab name${p.alias_count === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "unused";
}

function ExternalLabsAdmin() {
  const utils = trpc.useUtils();
  const { data: partners, isLoading } = trpc.tests.externalPartners.list.useQuery();
  const invalidate = () => utils.tests.externalPartners.list.invalidate();

  const remove = trpc.tests.externalPartners.delete.useMutation({
    onSuccess: () => {
      toast.success("Lab removed");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-olive-9">Loading…</div>;
  }

  return (
    <div className="flex flex-col grow min-h-0 gap-4 px-4 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-medium">External labs</h2>
          <p className="text-sm text-olive-11 max-w-3xl">
            Laboratories we send samples to. A lab's short code becomes the test
            code on every order placed with it, so it appears in test names and
            in sample formulas.
          </p>
        </div>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <PlusIcon />
            New lab
          </Button>
        )}
      </div>

      {creating && (
        <div className="border border-olive-6 bg-olive-1 p-3">
          <NewLabForm
            onDone={() => {
              setCreating(false);
              invalidate();
            }}
          />
        </div>
      )}

      <div className="border border-olive-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-olive-2">
              <Th>Name</Th>
              <Th className="w-32">Code</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-48">Contact</Th>
              <Th>Services</Th>
              <Th className="w-40">Used by</Th>
              <Th className="w-32">Changed</Th>
              <Th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {(partners ?? []).map((partner) => {
              const isEditing = editingId === partner.id;
              return (
                <tr
                  key={partner.id}
                  className={cn(
                    "border-b border-olive-4 align-top",
                    partner.status === ExternalTestPartnerStatusEnum.INACTIVE && "opacity-50",
                    isEditing && "bg-olive-2",
                  )}
                >
                  {isEditing ? (
                    <td colSpan={8} className="p-3">
                      <EditLabForm
                        partner={partner}
                        onDone={() => {
                          setEditingId(null);
                          invalidate();
                        }}
                      />
                    </td>
                  ) : (
                    <>
                      <td className="px-2 py-1.5">{partner.name}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{partner.code}</td>
                      <td className="px-2 py-1.5 text-olive-11">{statusLabel(partner.status)}</td>
                      <td className="px-2 py-1.5 text-olive-11">{partner.contact_person ?? ""}</td>
                      <td className="px-2 py-1.5 text-olive-11">
                        {partner.service_description ?? ""}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-olive-11">{usageSummary(partner)}</td>
                      <td className="px-2 py-1.5 text-xs text-olive-9 whitespace-nowrap">
                        {partner.last_updated_by_name ? `${partner.last_updated_by_name}, ` : ""}
                        {formatDate(partner.updated_at)}
                      </td>
                      <td className="p-1">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Edit"
                            onClick={() => setEditingId(partner.id)}
                          >
                            <PencilIcon />
                          </Button>
                          <DeleteConfirmDialog
                            title={`Remove ${partner.name}?`}
                            description={
                              partner.order_count > 0
                                ? `${partner.order_count} order${partner.order_count === 1 ? " has" : "s have"} been placed with this lab, so it cannot be removed. Set it to inactive instead.`
                                : `This lab has no orders. Its ${partner.alias_count} learnt lab name${partner.alias_count === 1 ? "" : "s"} will be removed with it.`
                            }
                            deleteLabel="Remove"
                            disabled={partner.order_count > 0}
                            onConfirm={() =>
                              remove.mutate(partner.id as ExternalTestPartnerId)
                            }
                          />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface LabDraft {
  name: string;
  code: string;
  status: ExternalTestPartnerStatusEnum;
  contact: string;
  services: string;
}

function draftOf(p: Partner): LabDraft {
  return {
    name: p.name,
    code: p.code,
    status: p.status,
    contact: p.contact_person ?? "",
    services: p.service_description ?? "",
  };
}

function LabFields({ value, onChange }: { value: LabDraft; onChange: (v: LabDraft) => void }) {
  return (
    <div className="grid grid-cols-[1fr_8rem_8rem_1fr_1fr] gap-2">
      <Field label="Name">
        <Input
          className="h-7"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </Field>
      <Field label="Code">
        <Input
          className="h-7 font-mono"
          placeholder="short_code"
          value={value.code}
          onChange={(e) => onChange({ ...value, code: e.target.value.trim() })}
        />
      </Field>
      <Field label="Status">
        <SimpleSelect
          value={value.status}
          options={STATUS_OPTIONS}
          onChange={(v) => onChange({ ...value, status: v as ExternalTestPartnerStatusEnum })}
        />
      </Field>
      <Field label="Contact">
        <Input
          className="h-7"
          placeholder="contact / email"
          value={value.contact}
          onChange={(e) => onChange({ ...value, contact: e.target.value })}
        />
      </Field>
      <Field label="Services">
        <Input
          className="h-7"
          placeholder="what they analyse"
          value={value.services}
          onChange={(e) => onChange({ ...value, services: e.target.value })}
        />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-olive-9">{label}</span>
      {children}
    </label>
  );
}

/**
 * Editing a lab. Descriptive fields save on Save; a code change renames every
 * test placed with the lab, so it is confirmed with that count first.
 */
function EditLabForm({ partner, onDone }: { partner: Partner; onDone: () => void }) {
  const [draft, setDraft] = useState<LabDraft>(draftOf(partner));
  const [confirming, setConfirming] = useState(false);
  const update = trpc.tests.externalPartners.update.useMutation({
    onSuccess: () => {
      toast.success(`${draft.name.trim()} saved`);
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });

  const codeChanged = draft.code !== partner.code;
  const changed = JSON.stringify(draft) !== JSON.stringify(draftOf(partner));
  const valid = draft.name.trim() !== "" && draft.code !== "";

  const save = () =>
    update.mutate({
      id: partner.id as ExternalTestPartnerId,
      name: draft.name.trim(),
      code: draft.code,
      status: draft.status,
      contact_person: draft.contact.trim() || null,
      service_description: draft.services.trim() || null,
    });

  return (
    <div className="flex flex-col gap-2">
      <LabFields value={draft} onChange={setDraft} />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!changed || !valid || update.isPending}
          onClick={() => (codeChanged ? setConfirming(true) : save())}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        {codeChanged && (
          <span className="text-xs text-olive-9">
            Changing the code renames every test placed with this lab; you will be asked to
            confirm.
          </span>
        )}
      </div>
      <ConfirmActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Rename ${partner.name}'s code?`}
        description="The code is the test code of every order placed with this lab. Test names and sample formulas that reference it will change."
        confirmLabel="Rename"
        isPending={update.isPending}
        onConfirm={() => {
          setConfirming(false);
          save();
        }}
      >
        <div className="flex flex-col gap-1 text-sm text-olive-11">
          <div className="font-mono text-xs text-olive-12">
            {partner.code} → {draft.code}
          </div>
          <div>This lab is used by {usageSummary(partner)}.</div>
        </div>
      </ConfirmActionDialog>
    </div>
  );
}

function NewLabForm({ onDone }: { onDone: () => void }) {
  const [draft, setDraft] = useState<LabDraft>({
    name: "",
    code: "",
    status: ExternalTestPartnerStatusEnum.ACTIVE,
    contact: "",
    services: "",
  });
  const create = trpc.tests.externalPartners.create.useMutation({
    onSuccess: () => {
      toast.success("Lab added");
      onDone();
    },
    onError: (err) => toast.error(err.message),
  });
  const valid = draft.name.trim() !== "" && draft.code !== "";
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">New lab</div>
      <LabFields value={draft} onChange={setDraft} />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({
              name: draft.name.trim(),
              code: draft.code,
              status: draft.status,
              contact_person: draft.contact.trim() || null,
              service_description: draft.services.trim() || null,
            })
          }
        >
          Add lab
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <span className="text-xs text-olive-9">
          Pick the code once: it becomes part of every test name for this lab.
        </span>
      </div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "border-b border-olive-6 px-2 py-1.5 text-left text-xs font-medium text-olive-11",
        className,
      )}
    >
      {children}
    </th>
  );
}
